import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ShieldWarning, CaretDown, CaretUp, Warning, XCircle, CheckCircle } from "@phosphor-icons/react";

const SEVERITY_STYLE = {
  fraud: "bg-red-50 text-red-800 border-red-200",
  warning: "bg-yellow-50 text-yellow-800 border-yellow-200",
  clean: "bg-green-50 text-green-700 border-green-200",
};

export default function FraudAlert({ claim }) {
  const [expanded, setExpanded] = useState(false);
  const docFraud = claim.document_fraud;
  const pages = claim.pages || [];

  // Count pages with fraud indicators
  const suspiciousPages = pages.filter(p => p.fraud_indicators?.is_suspicious);
  const hasPdfFraud = docFraud && docFraud.risk_level !== "clean";
  const hasPageFraud = suspiciousPages.length > 0;

  if (!hasPdfFraud && !hasPageFraud) return null;

  const totalFlags = (docFraud?.flags_count || 0) + suspiciousPages.length;
  const isHighRisk = docFraud?.risk_level === "fraud" || suspiciousPages.length > 2;

  return (
    <div className={`flex-shrink-0 border-b ${isHighRisk ? "bg-red-50 border-red-300" : "bg-yellow-50 border-yellow-300"}`} data-testid="fraud-alert">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-red-100/30 transition-colors"
        data-testid="fraud-alert-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isHighRisk ? "bg-red-200 border border-red-400" : "bg-yellow-200 border border-yellow-400"}`}>
            <ShieldWarning size={14} weight="bold" className={isHighRisk ? "text-red-700" : "text-yellow-700"} />
          </div>
          <span className={`text-sm font-bold ${isHighRisk ? "text-red-900" : "text-yellow-900"}`}>
            Document Fraud Check: {totalFlags} Flag{totalFlags !== 1 ? "s" : ""} Detected
          </span>
          {docFraud?.risk_level === "fraud" && (
            <Badge className="text-[10px] bg-red-600 text-white border-0 px-1.5 py-0 font-bold">HIGH RISK</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{expanded ? "Collapse" : "View details"}</span>
          {expanded ? <CaretUp size={14} className="text-gray-500" /> : <CaretDown size={14} className="text-gray-500" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-3">
          {/* PDF Metadata Flags */}
          {hasPdfFraud && docFraud.flags && (
            <div>
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">PDF Metadata Analysis</p>
              <div className="space-y-1.5">
                {docFraud.flags.filter(f => f.severity !== "clean").map((flag, idx) => (
                  <div key={`meta-${idx}`} className={`border rounded-md px-3 py-2 flex items-start gap-2 ${SEVERITY_STYLE[flag.severity] || SEVERITY_STYLE.warning}`}>
                    {flag.severity === "fraud"
                      ? <XCircle size={15} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
                      : <Warning size={15} weight="fill" className="text-yellow-600 mt-0.5 flex-shrink-0" />
                    }
                    <div className="min-w-0">
                      <p className={`text-xs font-bold ${flag.severity === "fraud" ? "text-red-900" : "text-yellow-900"}`}>{flag.check}</p>
                      <p className="text-xs opacity-80">{flag.detail}</p>
                      {flag.value && flag.value !== "empty" && (
                        <p className="text-[10px] font-mono opacity-60 mt-0.5">{flag.field}: {flag.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Metadata details */}
              {docFraud.metadata && (
                <div className="mt-2 border border-gray-200 rounded-md p-2 bg-white text-[10px] font-mono text-gray-500 grid grid-cols-2 gap-x-4 gap-y-0.5">
                  <span>Creator: {docFraud.metadata.creator || "—"}</span>
                  <span>Producer: {docFraud.metadata.producer || "—"}</span>
                  <span>Created: {docFraud.metadata.creation_date || "—"}</span>
                  <span>Modified: {docFraud.metadata.modification_date || "—"}</span>
                  <span>PDF Version: {docFraud.metadata.pdf_version || "—"}</span>
                  <span>Encrypted: {docFraud.metadata.is_encrypted ? "Yes" : "No"}</span>
                </div>
              )}
            </div>
          )}

          {/* Per-Page AI Fraud Detection */}
          {hasPageFraud && (
            <div>
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                AI Page Analysis — {suspiciousPages.length} Suspicious Page{suspiciousPages.length !== 1 ? "s" : ""}
              </p>
              <div className="space-y-1.5">
                {suspiciousPages.map(page => (
                  <div key={`page-${page.page_number}`} className="border border-red-200 bg-red-50 rounded-md px-3 py-2">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle size={14} weight="fill" className="text-red-600" />
                      <span className="text-xs font-bold text-red-900">
                        Page {page.page_number} — {(page.classification || "").replace(/_/g, " ")}
                      </span>
                      <Badge variant="outline" className="text-[9px] bg-red-100 text-red-700 border-red-300">
                        {Math.round((page.fraud_indicators.confidence || 0) * 100)}% suspicious
                      </Badge>
                    </div>
                    <ul className="text-xs text-red-800 space-y-0.5 pl-5">
                      {(page.fraud_indicators.findings || []).map((finding, fi) => (
                        <li key={fi} className="list-disc">{finding}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
